import { createSlice, PayloadAction, AnyAction } from "@reduxjs/toolkit";
import { Dayjs } from 'dayjs';
// Types
import Reducers from "app/types/Reducers";
// Models
import IBillingInvoice from "app/models/BillingInvoice";
import IInsuranceCase from "app/models/Case";
// Async
import { getInvoices, getInvoicesSummary, updateInvoice, payInvoice, exportInvoicesToPDF, getInvoicesSummaryBreakdown, getInvoicesSummaryBreakdownByAccount, createInterimInvoice } from './BillingInvoices.async';

interface IInvoiceSummary {
  count: number;
  amount: number;
}

interface IInvoiceSummaryBreakdown {
  outstanding: IInvoiceSummary;
  overdue: IInvoiceSummary;
  upcoming: IInvoiceSummary;
}

export interface IInvoiceSummaryBreakdownByAccount {
  account: { id: number; company: string; };
  breakdown: IInvoiceSummaryBreakdown
}

interface IState {
  invoices: any;
  invoicesTotal: number;
  invoicesSummary: IInvoiceSummary | null;
  invoicesSummaryBreakdown: IInvoiceSummaryBreakdown | null;
  invoicesSummaryBreakdownByAccount: IInvoiceSummaryBreakdownByAccount[] | null;
  invoicesSummaryBreakdownByAccountSort: {
    sortBy: string;
    direction: 'asc' | 'desc';
  };
  params: {
    accountId: string | null;
    case: Pick<IInsuranceCase, 'id' | 'name'> | null;
    search: string;
    status: string;
    dateRangeStart: Dayjs | null;
    dateRangeEnd: Dayjs | null;
    limit: number;
    offset: number;
    overdue: boolean | null;
  },
  batchInvoices: IBillingInvoice[];
  loading: boolean;
  interimInvoices: IBillingInvoice[] | null;
  invoicesExpandedAll: boolean;
};

const initialState:IState = {
  invoices: null,
  invoicesTotal: 0,
  invoicesSummary: null,
  invoicesSummaryBreakdown: null,
  invoicesSummaryBreakdownByAccount: null,
  invoicesSummaryBreakdownByAccountSort: {
    sortBy: 'outstanding',
    direction: 'desc'
  },
  params: {
    accountId: null,
    case: null,
    search: '',
    status: 'sent',
    dateRangeStart: null,
    dateRangeEnd: null,
    limit: 100,
    offset: 0,
    overdue: null
  },
  batchInvoices: [],
  loading: false,
  interimInvoices: null,
  invoicesExpandedAll: false
};

const slice = createSlice({
  name: Reducers.BillingInvoices,
  initialState,
  reducers: {
    toggleBatchInvoice: (state, action:PayloadAction<IBillingInvoice>) => {
      const invoiceId = action.payload.id;
      if (state.batchInvoices.some((invoice:IBillingInvoice) => invoice.id === invoiceId)) {
        state.batchInvoices = state.batchInvoices.filter((invoice:IBillingInvoice) => invoice.id !== invoiceId);
      } else {
        state.batchInvoices.push(action.payload);
      }
    },
    selectAllSentBatchInvoicesIds: (state) => {
      if ( state.invoices ){
        state.batchInvoices = state.invoices;
      }
    },
    setBatchInvoices: (state, action:PayloadAction<IBillingInvoice[]>) => {
      state.batchInvoices = action.payload;
    },
    setParams: (state, action:PayloadAction<any>) => {
      const params = action.payload;
      state.params = Object.keys(state.params).reduce((acc:any, cur:any) => {
        acc[cur] = (state.params as any)[cur];
        if ( typeof params[cur] !== 'undefined' ) acc[cur] = params[cur];
        return acc;
      }, {});
    },

    setInvoicesStatusPaid: (state, action:PayloadAction<number[]>) => {
      if ( state.invoices ){
        state.invoices = state.invoices.map((invoice:IBillingInvoice) => {
          if ( action.payload.includes(invoice.id) ) return { ...invoice, status: 'paid' }
          return invoice;
        });
      }
      state.batchInvoices = initialState['batchInvoices'];
    },

    // Create interim invoice
    addBillingInvoice: (state, action:PayloadAction<IBillingInvoice[]>) => {
      if ( state.invoices && action.payload && action.payload.length ){
        state.invoices = [...action.payload, ...state.invoices]
      }
    },

    setInvoicesSummaryBreakdownByAccountSort: (state, action:PayloadAction<{ sortBy: string; direction: 'asc' | 'desc'; }>) => {
      state.invoicesSummaryBreakdownByAccountSort = action.payload;
    },

    setInterimInvoices: (state, action:PayloadAction<IBillingInvoice[] | null>) => {
      state.interimInvoices = action.payload;
    },
    
    setInvoicesExpandedAll: (state, action:PayloadAction<boolean>) => {
      state.invoicesExpandedAll = action.payload;
    },

    // Default
    setInitialField: <IStateKey extends keyof IState>(state: IState, action: PayloadAction<IStateKey>) => {
      state[action.payload] = initialState[action.payload];
    },
    resetState: () => initialState
  },
  extraReducers(builder) {
    // Get invoices
    builder.addCase(getInvoices.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getInvoices.fulfilled, (state, action:PayloadAction<{ data:IBillingInvoice[], total:number }>) => {
      state.invoices = action.payload.data;
      state.invoicesTotal = action.payload.total;
    })
    // Get invoices summary
    builder.addCase(getInvoicesSummary.pending, (state) => {
      state.invoicesSummary = null;
    });
    builder.addCase(getInvoicesSummary.fulfilled, (state, action:PayloadAction<IInvoiceSummary>) => {
      state.invoicesSummary = action.payload;
    })
    // Get invoices summary breakdown
    builder.addCase(getInvoicesSummaryBreakdown.fulfilled, (state, action:PayloadAction<IInvoiceSummaryBreakdown>) => {
      state.invoicesSummaryBreakdown = action.payload;
    })
    // Get invoices summary breakdown by account
    builder.addCase(getInvoicesSummaryBreakdownByAccount.fulfilled, (state, action:PayloadAction<IInvoiceSummaryBreakdownByAccount[]>) => {
      state.invoicesSummaryBreakdownByAccount = action.payload;
    })
    // Create interim invoice
    builder.addCase(createInterimInvoice.fulfilled, (state, action) => {
      const isDryrun = action.meta.arg.params?.dryrun;
      const isSameCase = action.meta.arg.data?.caseId === state.params.case?.id;

      if (!isDryrun && isSameCase) {
        state.interimInvoices = null;
      }
    });
    // Update package
    builder.addCase(updateInvoice.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateInvoice.fulfilled, (state, action:PayloadAction<IBillingInvoice>) => {
      if ( state.invoices ){
        state.invoices = state.invoices.map((invoice:IBillingInvoice) => {
          if ( invoice.id === action.payload.id ) return action.payload;
          return invoice;
        })
      }
    })
    // Pay invoice
    builder.addCase(payInvoice.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(payInvoice.fulfilled, (state, action:PayloadAction<IBillingInvoice>) => {
      if ( state.invoices ){
        state.invoices = state.invoices.map((invoice:IBillingInvoice) => {
          if ( invoice.id === action.payload.id ) return action.payload;
          return invoice;
        })
      }
    })
    // Export invoices
    builder.addCase(exportInvoicesToPDF.pending, (state) => {
      state.loading = true;
    })

    builder.addMatcher(
      (action:AnyAction) => action.type.includes('fulfilled') || action.type.includes('rejected'),
      (state) => {
        state.loading = false
      }
    )
  },
});

export const BillingInvoicesActions = slice.actions;

export default slice.reducer;
